import React, { useEffect, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, Label } from "recharts";
import axiosInstance from "../../../../../Services/axiosCommon";
import { useNavigate } from "react-router-dom";

export default function PoSupplier() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function getData() {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/dashboard/PO-to-supplier");
      setData(res?.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  useEffect(() => {
    getData();
  }, []);

  const chartData = [
    { name: "Received", value: data?.received || 0, status: "Received" },
    { name: "Not Yet Shipped", value: data?.not_yet_shipped || 0, status: "Not Yet Shipped" },
    { name: "Under Shipment", value: data?.under_shipment || 0, status: "Under Shipment" },
    { name: "Under Clearance", value: data?.under_clearance || 0, status: "Under Clearance" },
  ];

  const handleSliceClick = (status) => {
    const path = "/shipping/shipment-tracking";
    navigate(path, { state: { shipmentStatus: status } });
  };

  const COLORS = ["#10B981", "#6B7280", "#F59E0B", "#6366F1"];
  const sumValue = chartData.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] transition-all duration-300 h-full flex flex-col justify-between">
      <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">PO to Supplier Status</h3>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-[10px] text-gray-400 font-medium">This Month: <strong className="text-emerald-600">{data?.this_month || 0}</strong></span>
          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/50">
            {data?.total || 0} Total
          </span>
        </div>
      </div>

      {loading ? (
        <div className="h-52 flex items-center justify-center animate-pulse">
          <div className="h-36 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center flex-1">
          {/* Donut Chart */}
          <div className="md:col-span-5 relative flex justify-center items-center h-44">
            {sumValue > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={58}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleSliceClick(e.status)}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <Label
                      value={`${data?.total || 0}`}
                      position="center"
                      style={{ fontSize: "15px", fontWeight: "bold", fill: "#1F2937" }}
                    />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      borderRadius: "8px",
                      color: "#FFF",
                      border: "none",
                      fontSize: "11px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 text-xs font-medium">No PO Data</div>
            )}
          </div>

          {/* Status List */}
          <div className="md:col-span-7 max-h-48 overflow-y-auto pr-1 space-y-2">
            {chartData.map((item, idx) => {
              const color = COLORS[idx % COLORS.length];
              const percent = sumValue > 0 ? Math.round((item.value / sumValue) * 100) : 0;
              return (
                <div
                  key={idx}
                  onClick={() => handleSliceClick(item.status)}
                  className="p-2.5 rounded-xl bg-gray-50/70 hover:bg-gray-100/80 cursor-pointer transition-all duration-200"
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                      <span className="text-xs font-semibold text-gray-700 truncate max-w-[130px]">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-gray-900">{item.value}</span>
                      <span className="text-[10px] text-gray-400 font-medium">{percent}%</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200/60 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${percent}%`, backgroundColor: color }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}